import React, { useEffect, useState } from 'react';
import { sensorAPI } from '../services/api';
import { DeviceStatus } from '../types';
import { format } from 'date-fns';
import { Wifi, WifiOff, RefreshCw, Download, Settings, Activity, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface DeviceManagementProps {
  onSelectDevice?: (deviceId: string) => void;
}

const DeviceManagement: React.FC<DeviceManagementProps> = ({ onSelectDevice }) => {
  const [devices, setDevices] = useState<DeviceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [exportingDevice, setExportingDevice] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('csv');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, 10000); // Rafraîchir toutes les 10s
    return () => clearInterval(interval);
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await sensorAPI.getStatus();
      setDevices(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des dispositifs:', error);
      if (loading) {
        toast.error('Impossible de charger les dispositifs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (deviceId: string) => {
    setRefreshing(deviceId);
    try {
      // Envoyer une commande de rafraîchissement
      await fetch(`${process.env.REACT_APP_API_URL}/sensors/refresh/${deviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      toast.success(`Rafraîchissement demandé pour ${deviceId}`);
      
      // Attendre un peu et rafraîchir l'affichage
      setTimeout(fetchDevices, 2000);
    } catch (error) {
      toast.error('Échec du rafraîchissement');
    } finally {
      setTimeout(() => setRefreshing(null), 3000);
    }
  };

  const handleExport = async (deviceId: string) => {
    setExportingDevice(deviceId);
    try {
      const params = new URLSearchParams({
        deviceId,
        from: dateRange.from + 'T00:00:00Z',
        to: dateRange.to + 'T23:59:59Z',
        format: exportFormat
      });
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sensors/history?${params}`,
        { headers: { 'Accept': exportFormat === 'csv' ? 'text/csv' : 'application/json' } }
      );
      
      if (exportFormat === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `historique-${deviceId}-${dateRange.from}.csv`;
        a.click();
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `historique-${deviceId}-${dateRange.from}.json`;
        a.click();
      }
      
      toast.success('Export réussi');
    } catch (error) {
      toast.error('Échec de l\'export');
    } finally {
      setExportingDevice(null);
    }
  };

  const handleExportAll = async () => {
    try {
      const params = new URLSearchParams({
        from: dateRange.from + 'T00:00:00Z',
        to: dateRange.to + 'T23:59:59Z',
        format: exportFormat
      });
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sensors/export?${params}`,
        { headers: { 'Accept': exportFormat === 'csv' ? 'text/csv' : 'application/json' } }
      );
      
      if (exportFormat === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export-complet-${dateRange.from}-${dateRange.to}.csv`;
        a.click();
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export-complet-${dateRange.from}-${dateRange.to}.json`;
        a.click();
      }
      
      toast.success('Export complet réussi');
    } catch (error) {
      toast.error('Échec de l\'export complet');
    }
  };

  const getSignalQuality = (signal: number | undefined) => {
    if (!signal) return { text: 'Inconnu', color: 'text-gray-500' };
    if (signal >= -50) return { text: 'Excellent', color: 'text-green-600 dark:text-green-400' };
    if (signal >= -60) return { text: 'Très bon', color: 'text-green-500 dark:text-green-300' };
    if (signal >= -70) return { text: 'Bon', color: 'text-yellow-500 dark:text-yellow-400' };
    if (signal >= -80) return { text: 'Moyen', color: 'text-orange-500 dark:text-orange-400' };
    return { text: 'Faible', color: 'text-red-500 dark:text-red-400' };
  };

  const formatUptime = (seconds: number | undefined) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}j ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec options d'export */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Gestion des Capteurs</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Surveillance et gestion de tous les capteurs d'humidité du sol
            </p>
          </div>
          
          {/* Contrôles d'export */}
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Du:</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Au:</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              />
            </div>
            
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'json' | 'csv')}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
            
            <button
              onClick={handleExportAll}
              className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
            >
              <Download className="h-4 w-4" />
              <span>Exporter tout</span>
            </button>
            
            <button
              onClick={fetchDevices}
              className="px-4 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2 text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Actualiser</span>
            </button>
          </div>
        </div>
      </div>

      {/* Grille des dispositifs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {devices.map(device => {
          const signalQuality = getSignalQuality(device.wifiSignal);
          const isOnline = device.status === 'online';
          const hasWarning = device.status === 'warning';
          
          return (
            <div
              key={device.deviceId}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                {/* En-tête du dispositif */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`h-3 w-3 rounded-full ${
                      isOnline ? 'bg-green-500 animate-pulse' : 
                      hasWarning ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {device.deviceId}
                    </h3>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isOnline 
                      ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : hasWarning
                      ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                      : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                  }`}>
                    {device.status === 'online' ? 'En ligne' : 
                     device.status === 'warning' ? 'Avertissement' : 'Hors ligne'}
                  </span>
                </div>

                {/* Informations du dispositif */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Signal WiFi</p>
                      <div className="flex items-center space-x-2">
                        {device.wifiSignal !== undefined ? (
                          <Wifi className="h-4 w-4 text-gray-500" />
                        ) : (
                          <WifiOff className="h-4 w-4 text-gray-500" />
                        )}
                        <span className={`font-medium ${signalQuality.color}`}>
                          {device.wifiSignal || 0} dBm ({signalQuality.text})
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Temps de fonctionnement</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatUptime(device.uptime)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Dernière mise à jour</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {device.lastUpdate ? format(new Date(device.lastUpdate), 'HH:mm:ss') : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Il y a {device.timeSinceUpdate}s
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Dernière valeur</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {device.lastValue || 'N/A'}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="flex space-x-2">
                  {onSelectDevice && (
                    <button
                      onClick={() => onSelectDevice(device.deviceId)}
                      className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <Activity className="h-4 w-4" />
                      <span>Voir détails</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleRefresh(device.deviceId)}
                    disabled={refreshing === device.deviceId || !isOnline}
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Rafraîchir"
                  >
                    <RefreshCw className={`h-4 w-4 ${refreshing === device.deviceId ? 'animate-spin' : ''}`} />
                  </button>
                  
                  <button
                    onClick={() => handleExport(device.deviceId)}
                    disabled={exportingDevice === device.deviceId}
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                    title="Exporter les données"
                  >
                    <Download className={`h-4 w-4 ${exportingDevice === device.deviceId ? 'animate-pulse' : ''}`} />
                  </button>
                </div>

                {/* Avertissements */}
                {hasWarning && (
                  <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Pas de mise à jour depuis plus d'une minute. Vérifier la connexion.
                    </p>
                  </div>
                )}
                
                {!isOnline && !hasWarning && (
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-300">
                      Dispositif hors ligne. Vérifier l'alimentation et la connexion réseau.
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Aucun dispositif */}
      {devices.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 text-center">
          <Settings className="h-12 w-12 text-yellow-600 dark:text-yellow-400 mx-auto mb-3" />
          <p className="text-yellow-800 dark:text-yellow-200 font-medium">
            Aucun dispositif détecté
          </p>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
            Assurez-vous que vos capteurs sont allumés et connectés au réseau
          </p>
        </div>
      )}
    </div>
  );
};

export default DeviceManagement;
